import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { debounce } from '@icp/utils';
import { AgTable } from '@icp/components';
import ACLDataSource from './ACLDataSource';
import { getRowsFromCachedData, setSelectedIfNeed } from './utils';

const EMPTY_ARRAY = [];

const ACLTableServerSideOneUrl = forwardRef(function ACLTableClientSide(props, ref) {
  const {
    aclDataSource,
    position,
    multiple,
    mapping,
    selectedKeys = EMPTY_ARRAY,
    stringEqual,
    listIdsUrl,
    onSelectionChanged,
    onLoadTotalCount,
    ...other
  } = props;

  const count = useRef(0);
  const gridRef = useRef(null);
  const requestBackup = useRef({});

  useImperativeHandle(ref, () => ({
    getSelectedKeys: () => {
      const { selectAll, toggledNodes } = gridRef.current.api.getServerSideSelectionState();
      const toggledKeys = stringEqual
        ? toggledNodes
        : toggledNodes.map((key) => {
            const rowNode = gridRef.current.api.getRowNode(key);
            if (rowNode) {
              return rowNode.data[mapping.value];
            }
            return key;
          });

      // 多选模式，点了 header 的 select all checkbox
      if (selectAll) {
        if (position === 'right') {
          return selectedKeys;
        }

        if (position === 'left') {
          // 如果所有的数据都已经加载完毕，直接返回 key 即可
          if (aclDataSource.cachedData.size === count.current + selectedKeys.length) {
            const ids = [...aclDataSource.cachedData.keys()];
            return ids.filter((id) => !toggledKeys.includes(id) && !selectedKeys.includes(id));
          }
          // 否则，如果配置了 listIdsUrl 的话则需要重新发请求拿所有 id
          if (listIdsUrl) {
            return aclDataSource.fetchAllIds(requestBackup.current).then((ids) => {
              return ids.filter((id) => !toggledKeys.includes(id) && !selectedKeys.includes(id));
            });
          }
          // 如果只配置了一个 listUrl，则只返回选中的行。因为懒加载可能会超大数据量，无法拿到所有 id
          const rows = gridRef.current.api.getSelectedRows();
          return rows.map((row) => (stringEqual ? String(row[mapping.value]) : row[mapping.value]));
        }
      }

      return toggledKeys;
    },
    deselectAll: () => {
      gridRef.current.api.deselectAll();
    },
  }));

  useEffect(() => {
    if (gridRef.current?.api) {
      // It seems like ag-grid with purge=false will never not send new request where there is no data.
      const purge = gridRef.current.api.getDisplayedRowCount() === 0;
      gridRef.current.api.refreshServerSide({ purge });
      if (!purge) {
        gridRef.current.api.showLoadingOverlay();
      } else {
        gridRef.current.api.hideOverlay();
      }
    }
  }, [selectedKeys]);

  const getRows = (request) => {
    requestBackup.current = request;

    // 多选，未选中任何数据，右侧直接显示空
    if (position === 'right' && !selectedKeys.length) {
      return Promise.resolve({ rowData: [], rowCount: 0 });
    }

    // 多选，右侧如果所有的选中数据都已经在 cache 里，直接显示就行
    if (position === 'right' && selectedKeys.length) {
      const cachedRows = getRowsFromCachedData(aclDataSource.cachedData, selectedKeys);
      if (cachedRows.length === selectedKeys.length) {
        return Promise.resolve({ rowData: cachedRows, rowCount: selectedKeys.length });
      }
    }

    let filterModel = request.filterModel;
    if (position && selectedKeys.length) {
      filterModel = (filterModel || []).concat({
        colId: mapping.value,
        filterType: 'set',
        type: position === 'left' ? 'notIn' : undefined,
        values: selectedKeys.map(String),
      });
    }

    return aclDataSource.fetchRowsSlice({ ...request, filterModel }).then((res) => {
      if (res.rowCount !== count.current) {
        count.current = res.rowCount;
        if (onLoadTotalCount) {
          onLoadTotalCount(res.rowCount + selectedKeys.length);
        }
      }

      setSelectedIfNeed(multiple, selectedKeys, gridRef.current.api);

      if (multiple && request.startRow === 0) {
        // auto size columns width according its content on the first data loaded
        setTimeout(() => {
          // 如果直接调用 autoSizeAllColumns 会导致 checkbox 列闪一下
          const colIds = gridRef.current.api.getColumns().map((column) => column.colId);
          gridRef.current.api.autoSizeColumns(colIds);
        }, 16);
      }

      return res;
    });
  };

  const handleSelectionChanged = debounce((params) => {
    const serverSideSelectionState = params.api.getServerSideSelectionState();
    const { selectAll, toggledNodes } = serverSideSelectionState;
    let hasSelect;

    if (selectAll) {
      hasSelect = toggledNodes.length !== count.current;
    } else {
      hasSelect = toggledNodes.length;
    }
    if (onSelectionChanged) {
      onSelectionChanged(hasSelect);
    }
  }, 50);

  return (
    <AgTable
      cacheBlockSize={30}
      suppressContextMenu={true}
      {...other}
      rowModelType="serverSide"
      getRows={getRows}
      getRowId={(params) =>
        params.data ? String(params.data[mapping.value] ?? params.data.id) : undefined
      }
      rowSelection={{
        mode: multiple ? 'multiRow' : 'singleRow',
        isRowSelectable: (params) => !!params.data?.[mapping.value],
        enableSelectionWithoutKeys: true,
      }}
      onSelectionChanged={handleSelectionChanged}
      ref={gridRef}
    />
  );
});

ACLTableServerSideOneUrl.propTypes = {
  aclDataSource: PropTypes.instanceOf(ACLDataSource),
  position: PropTypes.oneOf(['left', 'right']),
  multiple: PropTypes.bool,
  mapping: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
  }),
  selectedKeys: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  stringEqual: PropTypes.bool,
  listIdsUrl: PropTypes.string,
  onSelectionChanged: PropTypes.func,
  onLoadTotalCount: PropTypes.func,
  rowModelType: PropTypes.string,
  pagination: PropTypes.bool,
};

export default ACLTableServerSideOneUrl;
